import { IconTrash } from '@tabler/icons-react';
import type { QueryObserverResult } from '@tanstack/react-query';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { SerializableRenderTreeNode } from '../../../../../../react-commons/components/data-display/tree/RenderTreeNode';
import { Tree } from '../../../../../../react-commons/components/data-display/tree/Tree';
import { TreeHoverButton } from '../../../../../../react-commons/components/inputs/buttons/TreeHoverButton';
import { useNestedDialogFunction } from '../../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { SplitPane } from '../../../../../../react-commons/views/split-pane/SplitPane';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import type { StructuredSystemAnnouncementsOutline } from '../../../../resource/outlines/systemAnnouncementOutline';
import { useSystemAnnouncementActions } from '../../../../resource/resources/persistables/system/announcement/systemAnnouncementResource';
import type { RootState } from '../../../../store/rootReducer';
import { SystemAnnouncementCreationDialog } from '../../../reusable-components/dialogs/creation-dialogs/SystemAnnouncementCreationDialog';
import { useDeleteResourceDialogFunction } from '../../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { NothingSelectedComponent } from '../../../reusable-components/NothingSelectedComponent';
import { setUnsavedDataAction } from '../../../ui-state/local-status/localStatusActions';
import { defaultLoadingNode } from '../../../util/parseTreeFunctions';
import { selectIsDirty } from '../../../util/useSelectorFunctions';
import { selectSystemAnnouncement } from '../system-right-management/systemUserManagementActions';
import { SystemAnnouncementView } from './SystemAnnouncementView';

type SystemAnnouncementTreeViewTreeNode = SerializableRenderTreeNode<'expired' | 'active' | 'upcoming' | undefined>;

const parseTree = (
    outlineQuery: QueryObserverResult<StructuredSystemAnnouncementsOutline | null | undefined>
): Array<SystemAnnouncementTreeViewTreeNode> => {
    if (!outlineQuery.data) {
        return [defaultLoadingNode];
    }

    const expiredNodeChildren = outlineQuery.data.expired.map(
        (value): SystemAnnouncementTreeViewTreeNode => ({
            key: value.id,
            label: value.title,
            data: 'expired',
        })
    );

    const activeNodeChildren = outlineQuery.data.active.map(
        (value): SystemAnnouncementTreeViewTreeNode => ({
            key: value.id,
            label: value.title,
            data: 'active',
        })
    );

    const upcomingNodeChildren = outlineQuery.data.upcoming.map(
        (value): SystemAnnouncementTreeViewTreeNode => ({
            key: value.id,
            label: value.title,
            data: 'upcoming',
        })
    );

    const expiredNode: SystemAnnouncementTreeViewTreeNode = {
        key: 'expired',
        label: 'Expired',
        children: expiredNodeChildren,
        data: undefined,
    };

    const activeNode: SystemAnnouncementTreeViewTreeNode = {
        key: 'active',
        label: 'Active',
        children: activeNodeChildren,
        data: undefined,
    };

    const upcomingNode: SystemAnnouncementTreeViewTreeNode = {
        key: 'upcoming',
        label: 'Upcoming',
        children: upcomingNodeChildren,
        data: undefined,
    };

    return [expiredNode, activeNode, upcomingNode];
};

export const SystemAnnouncementTreeView = () => {
    const [selectedNode, setSelectedNode] = React.useState<SystemAnnouncementTreeViewTreeNode | null>(null);
    const [nodes, setNodes] = React.useState<Array<SystemAnnouncementTreeViewTreeNode>>([]);

    const dispatch = useDispatch();
    const isDirty = useSelector(selectIsDirty);
    const selectedNodeKey = useSelector(
        (state: RootState) => state.ui.systemUserManagement.selectedSystemAnnouncementId
    );

    const nestedDialog = useNestedDialogFunction();
    const deleteResource = useDeleteResourceDialogFunction().deleteResource;

    const systemAnnouncementActions = useSystemAnnouncementActions();

    const outlineQuery = systemAnnouncementActions.useGetStructuredSystemAnnouncement();
    const deleteSystemAnnouncement = systemAnnouncementActions.useDeleteResource();

    React.useEffect(() => {
        const newNodes = parseTree(outlineQuery);
        setNodes(newNodes);
    }, [isDirty, outlineQuery.data]);

    const deleteSystemElement = (id: string, name: string) => {
        deleteResource(id, name, ResourceType.SYSTEM_ANNOUNCEMENT, deleteSystemAnnouncement, () => {
            setSelectedNode(null);
        });
    };

    return (
        <div className={'main-window-content'}>
            <SplitPane secondaryInitialSize={70}>
                <div>
                    <Tree
                        nodes={nodes}
                        filtered
                        onNodeSelected={(node) => {
                            const nodeSelected = () => {
                                setSelectedNode(node);
                                dispatch(selectSystemAnnouncement(node.key));
                            };
                            if (isDirty) {
                                dispatch(setUnsavedDataAction(() => nodeSelected()));
                            } else {
                                nodeSelected();
                            }
                        }}
                        selectedNodeKey={selectedNodeKey}
                        onAddClick={() => {
                            nestedDialog.showNestedDialog(
                                <SystemAnnouncementCreationDialog
                                    onClose={nestedDialog.closeNestedDialog}
                                    readOnly={false}
                                />
                            );
                        }}
                        getElements={(node) =>
                            node.data ?
                                {
                                    hoverNode: (
                                        <TreeHoverButton
                                            Icon={IconTrash}
                                            onClick={() => deleteSystemElement(node.key, node.label)}
                                        />
                                    ),
                                }
                            :   {}
                        }
                    />
                </div>
                <div className={'right-side-display'}>
                    {selectedNodeKey === 'expired' || selectedNodeKey === 'active' || selectedNodeKey === 'upcoming' ?
                        <NothingSelectedComponent />
                    : selectedNodeKey ?
                        <SystemAnnouncementView id={selectedNodeKey} readOnly={selectedNode?.data !== 'upcoming'} />
                    :   <NothingSelectedComponent />}
                </div>
            </SplitPane>
        </div>
    );
};
